import { RestaurantCustomer, CUSTOMER_STATUS_LEVELS } from "@lemmonhq/types";

export const formatCurrency = (
  amount: number | undefined | null,
  currency: string = "CHF",
) =>
  new Intl.NumberFormat("de-CH", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(amount || 0));

export const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return "—";
  const d = new Date(dateString);
  return isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};

export const formatShortDate = (dateString: string | null | undefined) =>
  dateString
    ? new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    : "—";

export const formatTime = (dateString: string | null | undefined) =>
  dateString
    ? new Date(dateString).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : "";

export const getInitials = (
  name: string | null,
  email: string | null,
): string => {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return "??";
};

export const AVATAR_COLORS = [
  "from-violet-500 to-purple-700",
  "from-blue-500 to-indigo-700",
  "from-emerald-500 to-teal-700",
  "from-amber-500 to-orange-700",
  "from-rose-500 to-pink-700",
  "from-cyan-500 to-blue-700",
  "from-lime-500 to-emerald-700",
  "from-fuchsia-500 to-violet-700",
];

export const hashColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

export const formatTagLabel = (tag: string) =>
  tag
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

export const timeAgo = (
  dateStr: string | null | undefined,
  t?: (key: string, params?: (string | number)[]) => string,
): string => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const diff = Date.now() - d.getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return t?.("reservations.customers.today") ?? "Today";
  if (days === 1) return t?.("reservations.customers.yesterday") ?? "Yesterday";
  if (days < 7)
    return t?.("reservations.customers.days_ago", [days]) ?? `${days}d ago`;
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return t?.("reservations.customers.weeks_ago", [weeks]) ?? `${weeks}w ago`;
  }
  if (days < 365) {
    const months = Math.floor(days / 30);
    return (
      t?.("reservations.customers.months_ago", [months]) ?? `${months}mo ago`
    );
  }
  const years = Math.floor(days / 365);
  return t?.("reservations.customers.years_ago", [years]) ?? `${years}y ago`;
};

export const isVIP = (customer: RestaurantCustomer) =>
  customer.status_level === CUSTOMER_STATUS_LEVELS.VIP;

export const getTotalSpent = (customer: RestaurantCustomer) =>
  Number(customer.total_order_value || customer.total_spent || 0);

export const getResTime = (r: any): number => {
  const d = r.time || r.date;
  return d ? new Date(d).getTime() : 0;
};

/** Split a formatted short date like "Mar 26" into [month, day] */
export const splitShortDate = (
  dateString: string | null | undefined,
): [string, string] => {
  const formatted = formatShortDate(dateString);
  const parts = formatted.split(" ");
  return [parts[0] || "—", parts[1] || "—"];
};

// Reliability score color — use static class names for Tailwind JIT safety
export const getReliabilityColor = (
  pct: number,
): { text: string; bg: string } => {
  if (pct >= 80) return { text: "text-success", bg: "bg-success" };
  if (pct >= 50) return { text: "text-warning", bg: "bg-warning" };
  return { text: "text-error", bg: "bg-error" };
};
